(() => {
  'use strict';

  const instances = new WeakMap();

  const clampIndex = (index, length) => (index + length) % length;

  function initHero(root) {
    if (!root || instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const slides = [...root.querySelectorAll('[data-home-slide]')];
    const dots = [...root.querySelectorAll('[data-home-slide-to]')];
    const progress = root.querySelector('[data-home-hero-progress]');
    const pauseButton = root.querySelector('[data-home-hero-pause]');
    const prev = root.querySelector('[data-home-hero-prev]');
    const next = root.querySelector('[data-home-hero-next]');
    const autoplay = root.dataset.autoplay === 'true' && slides.length > 1;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const delay = Math.max(3000, Number(root.dataset.speed) || 6000);
    const pauseOnHover = root.dataset.pauseHover !== 'false';
    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = null;
    let pausedByUser = reducedMotion.matches;
    let hoverPaused = false;
    let focusPaused = false;
    let touchX = null;

    const setProgress = (animate = true) => {
      if (!progress) return;
      progress.style.transition = 'none';
      progress.style.transform = 'scaleX(0)';
      requestAnimationFrame(() => {
        if (!animate || !autoplay || pausedByUser || hoverPaused || focusPaused || document.hidden) return;
        progress.style.transition = `transform ${delay}ms linear`;
        progress.style.transform = 'scaleX(1)';
      });
    };

    const stop = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
      if (progress) {
        progress.style.transition = 'none';
        progress.style.transform = 'scaleX(0)';
      }
    };

    const schedule = () => {
      stop();
      if (!autoplay || pausedByUser || hoverPaused || focusPaused || document.hidden) return;
      setProgress(true);
      timer = window.setTimeout(() => show(current + 1, true), delay);
    };

    const syncMedia = (slide, active) => {
      slide.querySelectorAll('video').forEach((video) => {
        if (active && !document.hidden && !reducedMotion.matches) {
          const result = video.play();
          if (result?.catch) result.catch(() => {});
        } else {
          video.pause();
        }
      });
    };

    const show = (target, restart = true) => {
      if (!slides.length) return;
      current = clampIndex(target, slides.length);
      slides.forEach((slide, index) => {
        const active = index === current;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
          if (!el.hasAttribute('data-preserve-tabindex')) el.tabIndex = active ? 0 : -1;
        });
        syncMedia(slide, active);
      });
      dots.forEach((dot, index) => {
        const active = index === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      root.style.setProperty('--current-slide', current + 1);
      root.dispatchEvent(new CustomEvent('urbrand:hero-change', { detail: { index: current } }));
      if (restart) schedule();
    };

    const updatePauseLabel = () => {
      if (!pauseButton) return;
      const label = pausedByUser ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel;
      pauseButton.setAttribute('aria-label', label || (pausedByUser ? 'Reproduzir slides' : 'Pausar slides'));
      pauseButton.setAttribute('aria-pressed', pausedByUser ? 'true' : 'false');
      pauseButton.classList.toggle('is-paused', pausedByUser);
    };

    prev?.addEventListener('click', () => show(current - 1), { signal });
    next?.addEventListener('click', () => show(current + 1), { signal });
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.homeSlideTo)), { signal }));
    pauseButton?.addEventListener('click', () => {
      pausedByUser = !pausedByUser;
      updatePauseLabel();
      schedule();
    }, { signal });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(current - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(current + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        show(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        show(slides.length - 1);
      }
    }, { signal });

    root.addEventListener('touchstart', (event) => {
      touchX = event.changedTouches[0]?.clientX ?? null;
    }, { signal, passive: true });
    root.addEventListener('touchend', (event) => {
      if (touchX == null) return;
      const delta = (event.changedTouches[0]?.clientX ?? touchX) - touchX;
      touchX = null;
      if (Math.abs(delta) > 48) show(current + (delta < 0 ? 1 : -1));
    }, { signal, passive: true });

    if (pauseOnHover) {
      root.addEventListener('mouseenter', () => { hoverPaused = true; schedule(); }, { signal });
      root.addEventListener('mouseleave', () => { hoverPaused = false; schedule(); }, { signal });
    }
    root.addEventListener('focusin', () => { focusPaused = true; schedule(); }, { signal });
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) {
        focusPaused = false;
        schedule();
      }
    }, { signal });
    const syncPlayback = () => {
      slides.forEach((slide, index) => syncMedia(slide, index === current));
      schedule();
    };
    document.addEventListener('visibilitychange', syncPlayback, { signal });
    reducedMotion.addEventListener('change', () => {
      pausedByUser = reducedMotion.matches;
      updatePauseLabel();
      syncPlayback();
    }, { signal });

    show(current, false);
    updatePauseLabel();
    schedule();
    instances.set(root, { destroy: () => { stop(); controller.abort(); instances.delete(root); }, show });
  }

  function initTabs(root) {
    if (!root || instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const tabs = [...root.querySelectorAll('[data-home-tab]')];
    const panels = [...root.querySelectorAll('[data-home-tab-panel]')];

    const activate = (tab, moveFocus = false) => {
      const id = tab.dataset.homeTab;
      tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
        item.classList.toggle('is-active', selected);
      });
      panels.forEach((panel) => {
        const selected = panel.dataset.homeTabPanel === id;
        panel.hidden = !selected;
        panel.classList.toggle('is-active', selected);
      });
      if (moveFocus) tab.focus();
      requestAnimationFrame(() => window.URB?.refresh?.());
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab), { signal });
      tab.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex !== null) {
          event.preventDefault();
          activate(tabs[nextIndex], true);
        }
      }, { signal });
    });

    instances.set(root, { destroy: () => { controller.abort(); instances.delete(root); } });
  }

  function initCategoryGrid(root) {
    if (!root || instances.has(root) || root.dataset.randomImages !== 'true' || window.Shopify?.designMode) return;
    let destroyed = false;

    root.querySelectorAll('[data-home-category]').forEach((card) => {
      const image = card.querySelector('.home-category__media img');
      const poolNode = card.querySelector('[data-category-image-pool]');
      if (!image || !poolNode) return;

      let pool = [];
      try {
        pool = JSON.parse(poolNode.textContent || '[]');
      } catch (_) {
        return;
      }

      const unique = [...new Map(pool.filter((item) => item?.src).map((item) => [item.src, item])).values()];
      if (unique.length < 2) return;

      const storageKey = `urb:category-image:${root.id}:${card.dataset.categoryKey || ''}`;
      let previous = '';
      try { previous = sessionStorage.getItem(storageKey) || ''; } catch (_) { /* Storage may be unavailable. */ }
      const choices = unique.filter((item) => item.src !== previous);
      const candidates = choices.length ? choices : unique;
      const selected = candidates[Math.floor(Math.random() * candidates.length)];
      if (!selected) return;

      const preload = new Image();
      preload.decoding = 'async';
      preload.onload = () => {
        if (destroyed) return;
        image.classList.add('is-changing');
        if (selected.srcset) image.srcset = selected.srcset;
        else image.removeAttribute('srcset');
        image.src = selected.src;
        image.alt = selected.alt || image.alt;
        requestAnimationFrame(() => image.classList.remove('is-changing'));
        try { sessionStorage.setItem(storageKey, selected.src); } catch (_) { /* Storage may be unavailable. */ }
      };
      preload.sizes = image.sizes || '100vw';
      if (selected.srcset) preload.srcset = selected.srcset;
      preload.src = selected.src;
    });

    instances.set(root, { destroy: () => { destroyed = true; instances.delete(root); } });
  }

  function initFeaturedProducts(root) {
    if (!root || instances.has(root) || root.dataset.randomProducts !== 'true' || window.Shopify?.designMode) return;

    const grid = root.querySelector('[data-featured-product-grid]');
    const cards = grid ? [...grid.querySelectorAll(':scope > .product-card')] : [];
    const requestedCount = Number.parseInt(root.dataset.productCount || '0', 10);
    const visibleCount = Math.min(Math.max(requestedCount, 0), cards.length);
    if (!grid || cards.length < 2 || visibleCount < 1) return;

    const shuffle = (items) => {
      const result = [...items];
      for (let index = result.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
      }
      return result;
    };

    const chooseProducts = () => {
      const available = shuffle(cards.filter((card) => !card.querySelector('.badges .sold')));
      const soldOut = shuffle(cards.filter((card) => card.querySelector('.badges .sold')));
      const selected = available.slice(0, visibleCount);
      if (selected.length < visibleCount) selected.push(...soldOut.slice(0, visibleCount - selected.length));
      return selected;
    };

    const storageKey = `urb:featured-products:${root.id}`;
    let previous = '';
    try { previous = sessionStorage.getItem(storageKey) || ''; } catch (_) { /* Storage may be unavailable. */ }

    let selected = chooseProducts();
    let signature = selected.map((card) => card.dataset.productHandle || card.dataset.productUrl || '').join('|');
    for (let attempt = 0; attempt < 5 && signature === previous; attempt += 1) {
      selected = chooseProducts();
      signature = selected.map((card) => card.dataset.productHandle || card.dataset.productUrl || '').join('|');
    }

    const selectedSet = new Set(selected);
    const remaining = cards.filter((card) => !selectedSet.has(card));
    cards.forEach((card) => { card.hidden = true; });
    selected.forEach((card) => { card.hidden = false; });
    grid.append(...selected, ...remaining);
    root.dataset.randomReady = 'true';
    try { sessionStorage.setItem(storageKey, signature); } catch (_) { /* Storage may be unavailable. */ }

    instances.set(root, { destroy: () => instances.delete(root) });
  }

  function initSection(scope = document) {
    scope.querySelectorAll('[data-home-hero]').forEach(initHero);
    scope.querySelectorAll('[data-home-tabs]').forEach(initTabs);
    scope.querySelectorAll('.home-categories[data-random-images]').forEach(initCategoryGrid);
    scope.querySelectorAll('[data-featured-products]').forEach(initFeaturedProducts);
  }

  function destroySection(scope) {
    [scope, ...scope.querySelectorAll('[data-home-hero], [data-home-tabs], .home-categories[data-random-images], [data-featured-products]')].forEach((node) => {
      instances.get(node)?.destroy?.();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSection(), { once: true });
  } else {
    initSection();
  }

  document.addEventListener('shopify:section:load', (event) => initSection(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySection(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-home-slide]');
    const hero = slide?.closest('[data-home-hero]');
    if (hero && slide) instances.get(hero)?.show?.(Number(slide.dataset.homeSlideIndex), false);
    const tab = event.target.closest('[data-home-tab]');
    tab?.click();
  });
})();
